import { useThree } from '@react-three/fiber';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { type Object3D, Raycaster, SkinnedMesh, Vector2 } from 'three';

import { isMhrScene } from '@cartwhl/3d/gltf/utils';

import type { KeyPoseFkJointId } from '../types';
import { markPoseMeshSelectionPointerEvent } from './meshSelectionPointerEvents';
import {
  buildPoseBoneProxyPickEntries,
  getPoseBoneProxyPickCandidates,
} from './poseBoneProxyRaycast';
import { buildPoseMeshVertexGroups } from './poseMeshBoneResolver';
import {
  type PoseMeshHighlightState,
  applyPoseMeshHighlight,
  createPoseMeshHighlightState,
  isPoseMeshHighlightOverlay,
  restorePoseMeshHighlightState,
} from './poseMeshHighlight';
import type { PoseMeshPickEntry } from './poseMeshPickEntry';
import {
  buildPoseMeshSurfacePickEntries,
  intersectPoseMeshSurfacePickEntries,
} from './poseMeshSurfaceRaycast';
import type {
  MotionEditorBonePick,
  MotionEditorBonePickerSkeletonType,
  MotionEditorBoneSelectHandler,
} from './types';

interface MotionEditorBoneMeshPickerProps {
  enabled?: boolean;
  hoveredJointIds?: readonly KeyPoseFkJointId[];
  onBoneHover?: (pick: MotionEditorBonePick | null) => void;
  onBoneSelect?: MotionEditorBoneSelectHandler;
  raycastEnabled?: boolean;
  scene: Object3D | null;
  selectedJointIds?: readonly KeyPoseFkJointId[];
}

interface PointerPosition {
  clientX: number;
  clientY: number;
}

interface PointerClickState extends PointerPosition {
  moved: boolean;
  pointerId: number;
}

const pointer = new Vector2();
const BONE_PICK_CLICK_MOVE_THRESHOLD_PX = 12;
const BONE_PICK_POINTER_LISTENER_OPTIONS = { capture: true };
const EMPTY_JOINT_IDS: readonly KeyPoseFkJointId[] = [];
const MAX_BONE_PICK_PROXY_CANDIDATES = 6;

export default function MotionEditorBoneMeshPicker({
  enabled = true,
  hoveredJointIds: controlledHoveredJointIds,
  onBoneHover,
  onBoneSelect,
  raycastEnabled = true,
  scene,
  selectedJointIds = EMPTY_JOINT_IDS,
}: MotionEditorBoneMeshPickerProps) {
  const camera = useThree((state) => state.camera);
  const gl = useThree((state) => state.gl);
  const raycasterRef = useRef(new Raycaster());
  const highlightStateRef = useRef<PoseMeshHighlightState | null>(null);
  const hoveredJointIdRef = useRef<KeyPoseFkJointId | null>(null);
  const hoveredPickRef = useRef<MotionEditorBonePick | null>(null);
  const onBoneSelectRef = useRef(onBoneSelect);
  const visibleHoveredJointIdsRef = useRef<readonly KeyPoseFkJointId[]>([]);
  const selectedJointIdsRef =
    useRef<readonly KeyPoseFkJointId[]>(selectedJointIds);
  const [internalHoveredJointId, setInternalHoveredJointId] =
    useState<KeyPoseFkJointId | null>(null);
  const hoverControlled = controlledHoveredJointIds !== undefined;
  const internalHoveredJointIds = useMemo(
    () => (internalHoveredJointId ? [internalHoveredJointId] : EMPTY_JOINT_IDS),
    [internalHoveredJointId],
  );
  const visibleHoveredJointIds = hoverControlled
    ? (controlledHoveredJointIds ?? EMPTY_JOINT_IDS)
    : internalHoveredJointIds;
  const skeletonType = useMemo(() => resolveSkeletonType(scene), [scene]);
  const highlightEntries = useMemo(
    () => buildPosePickEntries(scene, skeletonType),
    [scene, skeletonType],
  );
  const proxyEntries = useMemo(
    () => buildPoseBoneProxyPickEntries(scene, skeletonType),
    [scene, skeletonType],
  );
  const surfaceEntries = useMemo(
    () => buildPoseMeshSurfacePickEntries(highlightEntries),
    [highlightEntries],
  );

  useLayoutEffect(() => {
    onBoneSelectRef.current = onBoneSelect;
  }, [onBoneSelect]);

  useEffect(() => {
    selectedJointIdsRef.current = selectedJointIds;
    visibleHoveredJointIdsRef.current = visibleHoveredJointIds;

    applyPoseMeshHighlight(
      highlightStateRef.current,
      visibleHoveredJointIds,
      selectedJointIds,
    );
  }, [selectedJointIds, visibleHoveredJointIds]);

  useEffect(() => {
    if (!enabled) {
      restorePoseMeshHighlightState(highlightStateRef.current);
      highlightStateRef.current = null;
      return;
    }

    const highlightState = createPoseMeshHighlightState(highlightEntries);
    highlightStateRef.current = highlightState;
    applyPoseMeshHighlight(
      highlightState,
      visibleHoveredJointIdsRef.current,
      selectedJointIdsRef.current,
    );

    return () => {
      restorePoseMeshHighlightState(highlightState);
      highlightStateRef.current = null;
    };
  }, [enabled, highlightEntries]);

  useEffect(() => {
    if (!enabled || !raycastEnabled) setHoveredPick(null, onBoneHover);
  }, [enabled, onBoneHover, raycastEnabled]);

  useEffect(() => {
    if (
      !enabled ||
      !raycastEnabled ||
      !scene ||
      proxyEntries.length === 0 ||
      surfaceEntries.length === 0
    ) {
      return;
    }

    const canvas = gl.domElement;
    let animationFrameId: number | null = null;
    let latestPointer: PointerPosition | null = null;
    let pointerClickState: PointerClickState | null = null;

    const pickAtPointer = (pointerPosition: PointerPosition) => {
      const rect = canvas.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) {
        return null;
      }

      pointer.x = ((pointerPosition.clientX - rect.left) / rect.width) * 2 - 1;
      pointer.y = -((pointerPosition.clientY - rect.top) / rect.height) * 2 + 1;

      const raycaster = raycasterRef.current;
      raycaster.setFromCamera(pointer, camera);
      const proxyCandidates = getPoseBoneProxyPickCandidates(
        proxyEntries,
        raycaster,
        MAX_BONE_PICK_PROXY_CANDIDATES,
      );
      if (proxyCandidates.length === 0) {
        return null;
      }

      const hit = intersectPoseMeshSurfacePickEntries(
        surfaceEntries,
        raycaster,
        proxyCandidates.map((candidate) => candidate.jointId),
        skeletonType,
      );
      if (!hit) {
        return null;
      }

      return {
        distance: hit.distance,
        jointId: hit.jointId,
        meshName: hit.meshName,
        point: hit.point,
        scene,
        skeletonType,
        sourceBoneName: hit.sourceBoneName,
      } satisfies MotionEditorBonePick;
    };
    const runPick = () => {
      animationFrameId = null;
      const pointerPosition = latestPointer;
      latestPointer = null;
      if (!pointerPosition) return;

      setHoveredPick(pickAtPointer(pointerPosition), onBoneHover);
    };
    const handlePointerDown = (event: PointerEvent) => {
      pointerClickState = {
        clientX: event.clientX,
        clientY: event.clientY,
        moved: false,
        pointerId: event.pointerId,
      };
    };
    const handlePointerMove = (event: PointerEvent) => {
      if (
        pointerClickState &&
        pointerClickState.pointerId === event.pointerId
      ) {
        const deltaX = event.clientX - pointerClickState.clientX;
        const deltaY = event.clientY - pointerClickState.clientY;
        const thresholdSquared =
          BONE_PICK_CLICK_MOVE_THRESHOLD_PX * BONE_PICK_CLICK_MOVE_THRESHOLD_PX;
        if (deltaX * deltaX + deltaY * deltaY > thresholdSquared) {
          pointerClickState.moved = true;
        }
      }

      latestPointer = {
        clientX: event.clientX,
        clientY: event.clientY,
      };
      if (animationFrameId !== null) return;

      animationFrameId = window.requestAnimationFrame(runPick);
    };
    const handlePointerUp = (event: PointerEvent) => {
      const clickState = pointerClickState;
      pointerClickState = null;
      if (
        !clickState ||
        clickState.pointerId !== event.pointerId ||
        clickState.moved
      ) {
        return;
      }

      const pointerUpPosition = {
        clientX: event.clientX,
        clientY: event.clientY,
      };
      const pick = hoveredPickRef.current ?? pickAtPointer(pointerUpPosition);
      if (!pick || !onBoneSelectRef.current) {
        return;
      }

      const selectionResult = onBoneSelectRef.current(pick);
      if (selectionResult === false) {
        return;
      }

      markPoseMeshSelectionPointerEvent(event);
    };
    const clearHover = () => {
      latestPointer = null;
      pointerClickState = null;
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
        animationFrameId = null;
      }
      setHoveredPick(null, onBoneHover);
    };

    canvas.addEventListener(
      'pointerdown',
      handlePointerDown,
      BONE_PICK_POINTER_LISTENER_OPTIONS,
    );
    canvas.addEventListener(
      'pointermove',
      handlePointerMove,
      BONE_PICK_POINTER_LISTENER_OPTIONS,
    );
    canvas.addEventListener(
      'pointerup',
      handlePointerUp,
      BONE_PICK_POINTER_LISTENER_OPTIONS,
    );
    canvas.addEventListener(
      'pointerleave',
      clearHover,
      BONE_PICK_POINTER_LISTENER_OPTIONS,
    );
    canvas.addEventListener(
      'pointercancel',
      clearHover,
      BONE_PICK_POINTER_LISTENER_OPTIONS,
    );

    return () => {
      canvas.removeEventListener(
        'pointerdown',
        handlePointerDown,
        BONE_PICK_POINTER_LISTENER_OPTIONS,
      );
      canvas.removeEventListener(
        'pointermove',
        handlePointerMove,
        BONE_PICK_POINTER_LISTENER_OPTIONS,
      );
      canvas.removeEventListener(
        'pointerup',
        handlePointerUp,
        BONE_PICK_POINTER_LISTENER_OPTIONS,
      );
      canvas.removeEventListener(
        'pointerleave',
        clearHover,
        BONE_PICK_POINTER_LISTENER_OPTIONS,
      );
      canvas.removeEventListener(
        'pointercancel',
        clearHover,
        BONE_PICK_POINTER_LISTENER_OPTIONS,
      );
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [
    camera,
    enabled,
    gl.domElement,
    hoverControlled,
    onBoneHover,
    proxyEntries,
    raycastEnabled,
    scene,
    skeletonType,
    surfaceEntries,
  ]);

  function setHoveredPick(
    pick: MotionEditorBonePick | null,
    hoverCallback?: (pick: MotionEditorBonePick | null) => void,
  ) {
    hoveredPickRef.current = pick;
    const nextJointId = pick?.jointId ?? null;
    if (hoveredJointIdRef.current === nextJointId) return;

    hoveredJointIdRef.current = nextJointId;
    if (!hoverControlled) {
      setInternalHoveredJointId(nextJointId);
    }
    hoverCallback?.(pick);
  }

  return null;
}

function buildPosePickEntries(
  scene: Object3D | null,
  skeletonType: MotionEditorBonePickerSkeletonType,
) {
  if (!scene) return [] as PoseMeshPickEntry[];

  const entries: PoseMeshPickEntry[] = [];
  scene.traverse((object) => {
    if (!(object instanceof SkinnedMesh)) return;
    if (isPoseMeshHighlightOverlay(object)) return;

    const geometry = object.geometry;
    if (
      !geometry.getAttribute('position') ||
      !geometry.getAttribute('skinIndex') ||
      !geometry.getAttribute('skinWeight') ||
      !object.skeleton?.bones?.length
    ) {
      return;
    }

    const vertexGroups = buildPoseMeshVertexGroups(object, skeletonType);
    if (vertexGroups.size === 0) return;
    entries.push({ mesh: object, vertexGroups });
  });

  return entries;
}

function resolveSkeletonType(
  scene: Object3D | null,
): MotionEditorBonePickerSkeletonType {
  return scene && isMhrScene(scene) ? 'mhr' : 'standard';
}
